import { useEffect, useMemo, useRef } from "react";
import { Box, Spinner } from "@salestrak/ui";

import type { NotificationRow as InboxStandardFragment } from "#app/lib/data";
import type { InboxDateGroup } from "#app/hooks/inbox/use-inbox-date-groups";
import { DateSeparator, DatePill, useDateJump } from "@salestrak/ui";
import { InboxNotificationItem } from "./inbox-notification-item";
import { InboxEmptyState } from "./inbox-empty-state";

interface InboxListProps {
  groups: InboxDateGroup[];
  /** All days with items, sorted descending — enables the jump-to-date popover. */
  dateKeys: string[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => Promise<unknown> | void;
  onItemClick: (item: InboxStandardFragment) => void;
  onClear: (item: InboxStandardFragment) => void;
  onUnclear: (item: InboxStandardFragment) => void;
  activeTab: "all" | "cleared";
}

export function InboxList({
  groups,
  dateKeys,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  onItemClick,
  onClear,
  onUnclear,
  activeTab,
}: InboxListProps) {
  const isCleared = activeTab === "cleared";
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Jump-to-date + floating sticky-date header, shared with the activity feed.
  const dateJumpGroups = useMemo(
    () => groups.map((g) => ({ dateKey: g.dateKey, dateLabel: g.dateLabel })),
    [groups],
  );
  const { scrollToDate, stickyDateLabel, isJumpingRef } = useDateJump({
    scrollRef,
    groups: dateJumpGroups,
    hasMore,
    loadMore: () => onLoadMore() ?? Promise.resolve(),
  });

  // Infinite scroll: observe a sentinel element near the bottom of the list.
  // When it enters the visible area (accounting for the scroller's overflow
  // clip), trigger the next page load. Skips while a jump is paginating so the
  // two loaders don't race.
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    if (!hasMore || loadingMore) return;

    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !isJumpingRef.current) {
          onLoadMore();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, onLoadMore, isJumpingRef]);

  if (loading) {
    return (
      <Box fill="flex" align="center" justify="center" paddingY="xl">
        <Spinner />
      </Box>
    );
  }

  if (groups.length === 0) {
    return (
      <Box fill="flex" align="center" justify="center">
        <InboxEmptyState activeTab={activeTab} />
      </Box>
    );
  }

  return (
    <Box direction="column" flex={1} minHeight={0} position="relative">
      {/* Floating date pill — appears when a date separator scrolls past the
          top. The wrapper spans the column so the pill centers, but only the
          pill itself takes the pointer. */}
      {stickyDateLabel && (
        <Box
          pointerEvents="none" position="absolute"
          top={8}
          left={0}
          right={0}
          zIndex={20}
          align="center"
        >
          <Box pointerEvents="auto">
            <DatePill
              label={stickyDateLabel}
              scrollToDate={scrollToDate}
              dateKeys={dateKeys}
            />
          </Box>
        </Box>
      )}

      {/* Plain overflow container, not a ScrollArea: useDateJump's scroll
          target and the pagination observer both read this element directly. */}
      <Box ref={scrollRef} fill="flex" overflow="auto">
        <Box direction="column" align="center" fill="width">
          <Box
            direction="column"
            paddingX="sm"
            paddingBottom="md"
            gap="sm"
            fill="width"
            maxWidth={1024}
          >
            {groups.map((group, groupIndex) => (
              <Box
                key={`${group.dateKey}-${groupIndex}`}
                direction="column"
                id={`date-group-${group.dateKey}`}
                gap="sm"
              >
                <DateSeparator
                  label={group.dateLabel}
                  scrollToDate={scrollToDate}
                  dateKeys={dateKeys}
                />
                {group.items.map((item, index) => (
                  <InboxNotificationItem
                    key={`${item.inboxId}-${index}`}
                    item={item}
                    onClick={onItemClick}
                    onClear={onClear}
                    onUnclear={onUnclear}
                    isCleared={isCleared}
                  />
                ))}
              </Box>
            ))}

            {/* Infinite scroll sentinel + loading indicator */}
            {hasMore && (
              <Box ref={sentinelRef} direction="column" paddingY="md" align="center">
                {loadingMore && <Spinner size="xl" />}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
